import { useCallback, useState } from "react";
import type { CmmdCloudProjectLifecyclePreflight } from "@cmmd-forge/contracts";

import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  refreshCmmdForgeRuntimeList,
  resolveSingletonConfig,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  CmmdForgeRuntimeApiClient,
  type CmmdForgeRuntimeLifecycleAction,
} from "../../environments/primary/cmmdForgeRuntimeApi";
import { connectCloudCatalogRow } from "../newThread/NewThreadLanding.cloud";
import { deriveCloudProjectKey } from "../sidebar/cloudProjectsGroup.logic";
import { toastManager } from "../ui/toast";
import { cmmdApiClient } from "../../environments/cmmdApi";
import { readForgeCloudEnvironmentLabelForRow } from "../../environments/forgeCloudEnvironmentLabel";
import {
  lifecycleActionTitle,
  lifecycleActionWord,
  lifecycleTargetName,
} from "./cloudLifecycleToastCopy";
import { parseCloudProjectLifecyclePreflight } from "./cloudProjectLifecyclePreflight";
import {
  deriveCloudEnvironmentRecovery,
  validateEnvironmentRecoveryReceipts,
} from "./cloudEnvironmentRecovery";
import { wakeCloudEnvironmentFromExecutionIntent } from "./wakeCloudEnvironmentFromExecutionIntent";
import { runCloudRecoveryWithProgressToast } from "./cloudRecoveryProgressToast";

interface CloudWorkspaceConnectOptions {
  readonly forceMaterialize: boolean;
  readonly successTitle: string;
  readonly failureTitle: string;
  readonly failureFallback: string;
}

const FORCE_UPDATE_POLL_MS = 2_000;
const FORCE_UPDATE_TIMEOUT_MS = 10 * 60 * 1_000;

async function waitForForceUpdate(
  client: CmmdForgeRuntimeApiClient,
  runtimeId: string,
  operationId: number,
): Promise<void> {
  const deadline = Date.now() + FORCE_UPDATE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, FORCE_UPDATE_POLL_MS));
    const operation = await client.getRuntimeCommitUpdateOperation({
      runtimeId,
      operationId,
    });
    if (operation.status === "completed") return;
    if (operation.status === "failed") {
      throw new Error(operation.error ?? "CMMD could not complete the Forge repair.");
    }
  }
  throw new Error("The Forge repair is still running. Refresh this page to check its final state.");
}

async function readRecoveryReceipts(
  rows: readonly CloudProjectCatalogRow[],
): Promise<readonly CmmdCloudProjectLifecyclePreflight[]> {
  return Promise.all(
    rows.map(async (row) =>
      parseCloudProjectLifecyclePreflight(
        await cmmdApiClient.getCloudProjectLifecyclePreflight(row.cmmdProjectId),
        row.cmmdProjectId,
      ),
    ),
  );
}

function requireRecoveryReceipt(input: {
  readonly receipts: readonly CmmdCloudProjectLifecyclePreflight[];
  readonly runtimeId: string | null;
  readonly phase: "before" | "after";
}): CmmdCloudProjectLifecyclePreflight {
  const receipt = validateEnvironmentRecoveryReceipts(input.receipts, input.runtimeId);
  if (receipt !== null) return receipt;
  const timing = input.phase === "before" ? "across its Checkout registrations" : "after recovery";
  throw new Error(`CMMD could not confirm one Environment ${timing}.`);
}

function requireSafeRecovery(receipt: CmmdCloudProjectLifecyclePreflight): void {
  const recovery = deriveCloudEnvironmentRecovery(receipt);
  if (recovery.outcome === "refused") throw new Error(recovery.reason);
}

function isWakeNowAction(
  row: CloudProjectCatalogRow | CloudEnvironmentCatalogRow,
  action: CmmdForgeRuntimeLifecycleAction,
): row is CloudEnvironmentCatalogRow {
  return action === "start" && !("cmmdProjectId" in row) && row.lifecycle?.state === "sleeping";
}

async function performCloudRuntimeLifecycleAction(input: {
  readonly row: CloudProjectCatalogRow | CloudEnvironmentCatalogRow;
  readonly runtimeId: string;
  readonly action: CmmdForgeRuntimeLifecycleAction;
  readonly wakeNow: boolean;
}): Promise<void> {
  if (input.wakeNow && !("cmmdProjectId" in input.row)) {
    await wakeCloudEnvironmentFromExecutionIntent(input.row);
    return;
  }
  const client = new CmmdForgeRuntimeApiClient(resolveSingletonConfig());
  await client.runRuntimeLifecycleAction({
    runtimeId: input.runtimeId,
    action: input.action,
  });
}

/** The Connections row actions. Each reports its outcome in a toast that names its target. */
export function useCloudWorkspaceActions(onConnectionSettled?: () => void) {
  // Keyed by project: the first connect can be busy before a runtime exists.
  const [busyCloudProjectKey, setBusyCloudProjectKey] = useState<string | null>(null);
  const [busyCloudActionLabel, setBusyCloudActionLabel] = useState<string | null>(null);

  const runCloudWorkspaceConnect = useCallback(
    async (row: CloudProjectCatalogRow, options: CloudWorkspaceConnectOptions) => {
      setBusyCloudProjectKey(deriveCloudProjectKey(row));
      try {
        await connectCloudCatalogRow(row, {
          // Other entry points surface progress. Settings needs only the terminal result.
          onProgress: () => undefined,
          forceMaterialize: options.forceMaterialize,
        });
        toastManager.add({
          type: "success",
          title: options.successTitle,
          description: `${row.projectName} is ready for agent work.`,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : options.failureFallback;
        toastManager.add({
          type: "error",
          title: options.failureTitle,
          description: message,
        });
      } finally {
        refreshCmmdForgeRuntimeList();
        onConnectionSettled?.();
        setBusyCloudProjectKey(null);
      }
    },
    [onConnectionSettled],
  );

  const addCloudEnvironment = useCallback(async () => {
    try {
      const client = new CmmdForgeRuntimeApiClient(resolveSingletonConfig());
      await client.ensureRuntime({});
      refreshCmmdForgeRuntimeList();
      toastManager.add({
        type: "success",
        title: "Cloud environment added",
        description: "Your Forge Cloud environment is ready for projects.",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to add cloud environment.";
      toastManager.add({
        type: "error",
        title: "Could not add cloud environment",
        description: message,
      });
    }
  }, []);

  const repairCloudWorkspace = useCallback(
    (row: CloudProjectCatalogRow) =>
      runCloudWorkspaceConnect(row, {
        forceMaterialize: true,
        successTitle: "Project repaired",
        failureTitle: "Could not repair project",
        failureFallback: "Failed to repair project.",
      }),
    [runCloudWorkspaceConnect],
  );

  const recoverCloudEnvironment = useCallback(
    async (environment: CloudEnvironmentCatalogRow, rows: readonly CloudProjectCatalogRow[]) => {
      setBusyCloudProjectKey(environment.runtimeId);
      try {
        await runCloudRecoveryWithProgressToast(
          async (onWaiting) => {
            requireRecoveryReceipt({
              receipts: await readRecoveryReceipts(rows),
              runtimeId: environment.runtimeId,
              phase: "before",
            });
            await wakeCloudEnvironmentFromExecutionIntent(environment, onWaiting);
            const finalReceipt = requireRecoveryReceipt({
              receipts: await readRecoveryReceipts(rows),
              runtimeId: environment.runtimeId,
              phase: "after",
            });
            requireSafeRecovery(finalReceipt);
            refreshCmmdForgeRuntimeList();
          },
          {
            setBusyActionLabel: setBusyCloudActionLabel,
            environmentName: readForgeCloudEnvironmentLabelForRow(environment),
          },
        );
      } finally {
        setBusyCloudProjectKey(null);
      }
    },
    [],
  );

  const runCloudRuntimeLifecycleAction = useCallback(
    async (
      row: CloudProjectCatalogRow | CloudEnvironmentCatalogRow,
      action: CmmdForgeRuntimeLifecycleAction,
    ) => {
      const runtimeId = row.runtimeId;
      if (runtimeId === null) return;
      setBusyCloudProjectKey("cmmdProjectId" in row ? deriveCloudProjectKey(row) : row.runtimeId);
      const targetName = lifecycleTargetName(row);
      try {
        const wakeNow = isWakeNowAction(row, action);
        await performCloudRuntimeLifecycleAction({
          row,
          runtimeId,
          action,
          wakeNow,
        });
        refreshCmmdForgeRuntimeList();
        toastManager.add({
          type: "success",
          title: lifecycleActionTitle(action, wakeNow, targetName),
          description: "The environment will update after CMMD confirms its state.",
        });
      } catch (error) {
        const actionWord = lifecycleActionWord(action);
        const message =
          error instanceof Error ? error.message : `Failed to ${actionWord} ${targetName}.`;
        toastManager.add({
          type: "error",
          title: `Could not ${actionWord} ${targetName}`,
          description: message,
        });
      } finally {
        setBusyCloudProjectKey(null);
      }
    },
    [],
  );

  const updateCloudRuntime = useCallback(
    async (environment: CloudEnvironmentCatalogRow): Promise<boolean> => {
      const runtimeId = environment.runtimeId;
      if (runtimeId === null) return false;
      setBusyCloudProjectKey(runtimeId);
      const targetName = readForgeCloudEnvironmentLabelForRow(environment);
      try {
        const client = new CmmdForgeRuntimeApiClient(resolveSingletonConfig());
        const operation = await client.updateRuntimeCommit({ runtimeId });
        await waitForForceUpdate(client, runtimeId, operation.id);
        refreshCmmdForgeRuntimeList();
        toastManager.add({
          type: "success",
          title: `Forge repaired on ${targetName}`,
          description:
            "The Environment is running the current Forge version. Paused conversations can resume.",
        });
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to repair Forge.";
        toastManager.add({
          type: "error",
          title: `Could not repair Forge on ${targetName}`,
          description: message,
        });
        return false;
      } finally {
        setBusyCloudProjectKey(null);
      }
    },
    [],
  );

  return {
    busyCloudProjectKey,
    busyCloudActionLabel,
    addCloudEnvironment,
    repairCloudWorkspace,
    recoverCloudEnvironment,
    runCloudRuntimeLifecycleAction,
    updateCloudRuntime,
  } as const;
}
